import {
    Container,
    Header,
    KeyValuePairs,
    Link as CloudScapeLink,
    FormField,
    Select,
    Button,
} from '@amzn/awsui-components-react-v3';
import { useActivityGroupDeliveryWeekOptions } from '../../hooks/useActivityGroupDeliveryWeekOptions';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { Link } from 'react-router-dom';
import { ActivityStatus } from '../../../Common/Common';
import { useActivityGroup } from '../../hooks/hooks';
import { extractDeliveryName } from '../../utils/extractDeliveryName';
import { getActivityDetailsItems } from './getActivityDetailsItems';
import { ActivityGroupDeliveryDetailsSessionTable } from './ActivityGroupDeliverySessionTable';

export type FilteredSession = {
    start_timestamp: number;
    instructors?: Array<{ name: string; pk: string }>;
    activityName: string;
    instructorNamesString: string;
    instructorsToPkObject: { [key: string]: string };
    pk: string;
};

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const ActivityGroupDeliveryDetails = () => {
    const activityGroup = useActivityGroup();
    const {
        status,
        child_activities: childActivities,
        name,
        pk,
    } = activityGroup || {};

    const { weekOptionsForDates, selectedWeek, handleWeekChange } =
        useActivityGroupDeliveryWeekOptions({
            start: activityGroup.start_timestamp,
            end: activityGroup.end_timestamp,
            timezone: activityGroup.selected_timezone,
            includeAllOption: false,
        });

    const getFilteredSessions = (): FilteredSession[] => {
        if (!childActivities || childActivities.length === 0) {
            return [];
        }

        const weekStart = dayjs(selectedWeek.value);
        const weekEnd = weekStart.add(1, 'week');

        return childActivities
            .flatMap((activity) => {
                return (activity.delivery_sessions || []).map((session) => ({
                    ...session,
                    timezone: activity.delivery_timezone,
                    activityName: extractDeliveryName(activity.activity_name),
                    instructorNamesString: session.instructors
                        .map((instructor) => instructor.name)
                        .join(','),
                    instructorsToPkObject: session.instructors.reduce(
                        (accumulator, instructor) => {
                            accumulator[instructor.name] = instructor.pk;
                            return accumulator;
                        },
                        {},
                    ),
                    pk: activity.pk,
                }));
            })
            .filter((session) => {
                const sessionDate = dayjs.unix(session.start_timestamp);
                return (
                    sessionDate.isSameOrAfter(weekStart) &&
                    sessionDate.isSameOrBefore(weekEnd)
                );
            });
    };
    if (!activityGroup || !childActivities || childActivities.length === 0) {
        return null;
    }

    const filteredSessions = getFilteredSessions();

    return (
        <div>
            <Container
                header={
                    <Header
                        actions={
                            <Link
                                to={`/activities/group/${pk}/edit-delivery-details`}
                            >
                                <Button
                                    disabled={
                                        status === ActivityStatus.Canceled
                                    }
                                    disabledReason="Cannot edit a canceled activity group."
                                    data-testid="EditButtonActivityGroup"
                                >
                                    Edit
                                </Button>
                            </Link>
                        }
                        variant="h2"
                    >
                        Delivery details
                    </Header>
                }
                variant="stacked"
            >
                <KeyValuePairs
                    columns={3}
                    items={getActivityDetailsItems(childActivities[0])}
                />
            </Container>

            <FormField stretch={true}>
                <Select
                    options={weekOptionsForDates}
                    selectedOption={selectedWeek}
                    onChange={({ detail }) =>
                        handleWeekChange(detail.selectedOption)
                    }
                />
            </FormField>

            <ActivityGroupDeliveryDetailsSessionTable
                filteredSessions={filteredSessions}
            />
        </div>
    );
};

export default ActivityGroupDeliveryDetails;
